import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HealthCheckDto, HealthChecksResponse, HealthCheckStatus } from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, type DataTableColumn, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiGet } from '../../lib/api';
import { cloudSidebar } from './cloudSidebar';

const STATUS: Record<HealthCheckStatus, { label: string; variant: 'success' | 'danger' | 'secondary'; icon: string }> = {
  healthy: { label: 'Healthy', variant: 'success', icon: 'ph-check-circle' },
  unhealthy: { label: 'Unhealthy', variant: 'danger', icon: 'ph-x-circle' },
  unknown: { label: 'Unknown', variant: 'secondary', icon: 'ph-question' },
};

export function CloudHealthChecksPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [data, setData] = useState<HealthChecksResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setErr(null);
    apiGet<HealthChecksResponse>('/api/cloud/health-checks')
      .then(setData)
      .catch(() => setErr('Could not load health checks.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const open = (h: HealthCheckDto) => navigate(`/cloud/health-checks/${h.id}`);

  const columns: DataTableColumn<HealthCheckDto>[] = [
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (h) => h.status,
      render: (h) => {
        const s = STATUS[h.status];
        return (
          <span className={`badge bg-${s.variant} bg-opacity-25 text-${s.variant}`}>
            <i className={`${s.icon} me-1`} />
            {s.label}
          </span>
        );
      },
    },
    { key: 'name', header: 'Name', sortable: true, render: (h) => <span className="fw-semibold">{h.name || h.id}</span> },
    { key: 'type', header: 'Type', sortable: true, render: (h) => <span className="badge bg-light text-body border">{h.type}</span> },
    { key: 'endpoint', header: 'Endpoint', render: (h) => <span className="font-monospace text-truncate d-inline-block" style={{ maxWidth: 420 }}>{h.endpoint}</span> },
    { key: 'requestInterval', header: 'Interval', align: 'end', render: (h) => (h.requestInterval == null ? '—' : `${h.requestInterval}s`) },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (h) => (
        <Button variant="light" size="sm" icon="ph-arrow-right" onClick={() => open(h)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <AppChrome secondarySidebar={cloudSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Cloud Management - <span className="fw-normal">Health Check</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Cloud Management' }, { label: 'Health Check' }]} />}
        actions={
          <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
            Refresh
          </Button>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}
        {data && !data.configured && (
          <Alert variant="info" icon="ph-info">
            AWS isn’t configured — add AWS credentials (with Route 53 health-check access) to see health checks.
          </Alert>
        )}
        {data?.error && (
          <Alert variant="warning" icon="ph-warning">
            AWS Route 53: {data.error}
          </Alert>
        )}

        {data === null && loading ? (
          <Spinner />
        ) : data && data.checks.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center text-muted py-5">No health checks found.</div>
          </div>
        ) : data ? (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={data.checks}
              getRowKey={(h) => h.id}
              searchable
              searchPlaceholder="Search health checks…"
              initialSort={{ key: 'name', dir: 'asc' }}
              onRowClick={open}
            />
          </div>
        ) : null}
      </div>
    </AppChrome>
  );
}
